import { Component, inject, output } from '@angular/core';
import { Router } from '@angular/router';
import { UwIcon } from '../../shared/ui/icon/uw-icon';
import { UwButton } from '../../shared/ui/button/uw-button';
import { NotificationService } from '../../core/services/notification.service';
import type { AppNotification } from '../../core/models/notification.model';

const KIND_ICONS: Record<AppNotification['kind'], string> = {
  task: 'clipboard',
  ai: 'sparkles',
  rule: 'shield',
  message: 'message-square',
};

const KIND_CLASSES: Record<AppNotification['kind'], string> = {
  task: 'bg-primary-soft text-primary',
  ai: 'bg-ai-soft text-ai',
  rule: 'bg-warning-soft text-warning',
  message: 'bg-info-soft text-info',
};

@Component({
  selector: 'uw-notifications-dropdown',
  standalone: true,
  imports: [UwIcon, UwButton],
  template: `
    <div class="absolute right-0 top-[calc(100%+10px)] z-popover w-[360px] max-w-[calc(100vw-24px)] overflow-hidden rounded-xl border border-line bg-surface-elevated shadow-lg">
      <header class="flex items-center justify-between border-b border-line px-4 py-3">
        <h3 class="flex items-center gap-1.5 text-[13px] font-semibold text-primary">
          <uw-icon name="bell" [size]="14" class="text-ai" />
          Notifications
          @if (notificationService.unreadCount() > 0) {
            <span class="rounded-full bg-ai-soft px-1.5 text-[10px] font-bold text-ai">{{ notificationService.unreadCount() }}</span>
          }
        </h3>
        <button
          class="text-[11px] font-semibold text-text-muted transition-colors hover:text-ai"
          (click)="notificationService.markAllRead()"
        >
          Mark all read
        </button>
      </header>

      <div class="max-h-[380px] overflow-y-auto">
        @if (notificationService.notifications().length === 0) {
          <p class="px-4 py-8 text-center text-[12px] text-text-muted">You're all caught up.</p>
        } @else {
          @for (n of notificationService.notifications(); track n.id) {
            <button
              class="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-sunken"
              (click)="open(n)"
            >
              <span class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" [class]="KIND_CLASSES[n.kind]">
                <uw-icon [name]="KIND_ICONS[n.kind]" [size]="13" />
              </span>
              <div class="min-w-0 flex-1">
                <p class="flex items-center gap-1.5 text-[12px] font-semibold text-primary">
                  {{ n.title }}
                  @if (!n.read) {
                    <span class="h-1.5 w-1.5 shrink-0 rounded-full bg-ai"></span>
                  }
                </p>
                <p class="mt-0.5 text-[11px] leading-snug text-secondary">{{ n.body }}</p>
                <p class="mt-1 text-[10px] text-text-muted">{{ n.time }}</p>
              </div>
            </button>
          }
        }
      </div>

      <footer class="border-t border-line bg-surface-sunken/50 px-4 py-2">
        <uw-button variant="ghost" size="xs" icon="arrow-right" (click)="viewAll()">View all activity</uw-button>
      </footer>
    </div>
  `,
})
export class UwNotificationsDropdown {
  protected readonly KIND_ICONS = KIND_ICONS;
  protected readonly KIND_CLASSES = KIND_CLASSES;

  protected readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);

  readonly closed = output();

  protected open(n: AppNotification): void {
    this.notificationService.markRead(n.id);
    if (n.route) {
      this.closed.emit();
      void this.router.navigate([n.route]);
    }
  }

  protected viewAll(): void {
    this.closed.emit();
    void this.router.navigate(['/classic/analytics']);
  }
}
